import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StatusBadge from "@/components/app/StatusBadge";
import { Search, Eye, GraduationCap, FileSpreadsheet } from "lucide-react";

const Field = ({ label, value, mono }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
    <div className={`mt-1 text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate-400">—</span>}</div>
  </div>
);

export default function PartnerEnrollments() {
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [cid, setCid] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (cid !== "all") params.course_id = cid;
    if (status !== "all") params.status = status;
    api.get("/enrollments", { params }).then((r) => setItems(r.data));
  }, [cid, status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)); }, []);

  const filtered = useMemo(() => items.filter((e) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return `${e.first_name} ${e.last_name} ${e.email} ${e.fiscal_code} ${e.order_number || ""}`.toLowerCase().includes(s);
  }), [items, q]);

  return (
    <div className="space-y-6" data-testid="partner-enrollments">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Iscrizioni</h1>
          <p className="text-slate-500 mt-1">Tutti i corsisti iscritti dai tuoi ordini.</p>
        </div>
        <Button asChild className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="partner-enrollments-export-xlsx">
          <a href={`${API}/export/enrollments.xlsx?${new URLSearchParams({ ...(cid !== "all" ? { course_id: cid } : {}), ...(status !== "all" ? { status } : {}) }).toString()}`} target="_blank" rel="noopener noreferrer">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Scarica Excel
          </a>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca per nome, email, CF, ordine…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="partner-enroll-search" />
        </div>
        <Select value={cid} onValueChange={setCid}>
          <SelectTrigger data-testid="partner-enroll-course-filter"><SelectValue placeholder="Corso" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i corsi</SelectItem>
            {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="partner-enroll-status-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="iscritto">Iscritto</SelectItem>
            <SelectItem value="confermato">Confermato</SelectItem>
            <SelectItem value="completato">Completato</SelectItem>
            <SelectItem value="annullato">Annullato</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ordine</TableHead>
              <TableHead>Corsista</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Corso</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((e) => (
              <TableRow key={e.id} data-testid={`partner-enroll-${e.id}`}>
                <TableCell className="font-mono text-xs text-slate-600">
                  {e.order_id ? (
                    <Link to={`/partner/ordini/${e.order_id}`} className="hover:underline text-[#0F2942] font-semibold">
                      {e.order_number || "—"}
                    </Link>
                  ) : (e.order_number || "—")}
                </TableCell>
                <TableCell className="font-medium whitespace-nowrap">{e.first_name} {e.last_name}</TableCell>
                <TableCell className="text-sm">{e.email}</TableCell>
                <TableCell>{e.course_title}</TableCell>
                <TableCell className="text-xs text-slate-600 whitespace-nowrap">{dateIt(e.created_at)}</TableCell>
                <TableCell><StatusBadge status={e.status} /></TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button variant="outline" size="sm" onClick={() => setDetail(e)} data-testid={`partner-view-enroll-${e.id}`}>
                    <Eye className="w-4 h-4 mr-1" /> Scheda
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">Nessuna iscrizione.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="partner-enroll-detail-dialog">
          <DialogHeader>
            <DialogTitle>Scheda iscritto · {detail?.first_name} {detail?.last_name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Numero ordine" value={detail.order_number} mono />
                <Field label="Stato" value={detail.status} />
                <Field label="Corso" value={detail.course_title} />
                <Field label="Data iscrizione" value={dateIt(detail.created_at)} />
                <Field label="Nome" value={detail.first_name} />
                <Field label="Cognome" value={detail.last_name} />
                <Field label="Codice fiscale" value={detail.fiscal_code} mono />
                <Field label="Data di nascita" value={detail.birth_date ? dateIt(detail.birth_date) : ""} />
                <Field label="Luogo di nascita" value={detail.birth_place} />
                <Field label="Email" value={detail.email} />
                <Field label="Telefono" value={detail.phone} />
                <Field label="Azienda" value={detail.company} />
                <Field label="Mansione" value={detail.role} />
              </div>
              <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/60">
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="w-4 h-4 text-amber-600" />
                  <div className="text-sm font-bold text-slate-800">Crediti Formativi Professionali</div>
                  {detail.requires_cfp
                    ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Richiesti</Badge>
                    : <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Non richiesti</Badge>}
                </div>
                {detail.requires_cfp && (
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Profilo professionale" value={detail.professional_profile} />
                    <Field label="Provincia dell'ordine" value={detail.professional_province} />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                {detail.order_id && (
                  <Link to={`/partner/ordini/${detail.order_id}`}>
                    <Button variant="outline">Vai all'ordine</Button>
                  </Link>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
